import { isTslhTenant } from '../lib/tenantStore';
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../lib/userStore';
import { useLeagueStore } from '../lib/leagueStore';
import { useEmbedStore } from '../lib/embedStore';
import { supabase } from '../lib/supabase';

/**
 * Composant qui restaure automatiquement la dernière ligue utilisée
 * et redirige l'utilisateur vers le classement de cette ligue au chargement de l'app.
 * 
 * La dernière ligue est toujours restaurée, indépendamment du "remember me",
 * car leagueStore persiste toujours dans localStorage.
 */
export function AutoLeagueRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { username, user_id, setCurrentLeagueId } = useUserStore();
  const { selectedLeague, setSelectedLeague } = useLeagueStore();
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const hasAttemptedRedirect = useRef(false);
  const lastValidatedUserId = useRef<number | null>(null);

  // Réinitialiser le flag de redirection quand l'utilisateur change
  useEffect(() => {
    if (user_id !== lastValidatedUserId.current) {
      hasAttemptedRedirect.current = false;
      lastValidatedUserId.current = user_id as number | null;
    }
  }, [user_id]);

  useEffect(() => {
    // Mode normal (hors embed) : ne jamais auto-charger /dashboard, même avec une ligue sauvegardée
    if (!isEmbed || isTslhTenant()) return;

    // Ne tenter la redirection qu'une seule fois par session par utilisateur
    if (hasAttemptedRedirect.current) return;
    
    // Ne rien faire si l'utilisateur n'est pas connecté
    if (!username || !user_id) return;

    // Ne rien faire si on est déjà sur une page autre que landing/auth/dashboard.
    // Important: ne pas auto-rediriger depuis /standings, sinon un clic sur Classement
    // renvoie automatiquement vers le dashboard.
    const isLandingPage =
      location.pathname === '/' ||
      location.pathname === '/auth' ||
      location.pathname === '/dashboard';
    if (!isLandingPage) return;

    // Si on a déjà une ligue sélectionnée (depuis localStorage), valider et rediriger
    if (selectedLeague && selectedLeague.id) {
      hasAttemptedRedirect.current = true;
      // Valider que l'utilisateur est toujours membre, puis rediriger
      validateAndRestore(selectedLeague.id);
      return;
    }
  }, [username, user_id, selectedLeague, location.pathname, navigate, setCurrentLeagueId, setSelectedLeague, isEmbed]);

  const validateAndRestore = async (leagueId: number) => {
    try {
      // Vérifier que l'utilisateur est toujours membre de cette ligue
      const { data: membership, error: membershipError } = await supabase
        .from('leagues_users')
        .select('league_id')
        .eq('league_id', leagueId)
        .eq('user_id', user_id)
        .maybeSingle();

      if (membershipError && membershipError.code !== 'PGRST116') {
        console.error('Erreur lors de la vérification de membership:', membershipError);
        // Effacer les données invalides
        setCurrentLeagueId(null);
        setSelectedLeague(null);
        return;
      }

      // Si l'utilisateur n'est plus membre, effacer
      if (!membership) {
        console.log('Utilisateur plus membre de la ligue, nettoyage');
        setCurrentLeagueId(null);
        setSelectedLeague(null);
        return;
      }

      // Vérifier que la ligue existe encore
      const { data: leagueData, error: leagueError } = await supabase
        .from('leagues')
        .select('league_id, name')
        .eq('league_id', leagueId)
        .single();

      if (leagueError) {
        console.error('Erreur lors de la récupération de la ligue:', leagueError);
        // Ligue n'existe plus, effacer
        setCurrentLeagueId(null);
        setSelectedLeague(null);
        return;
      }

      // Tout est valide, synchroniser currentLeagueId et rediriger
      setCurrentLeagueId(leagueData.league_id);
      
      // Rediriger vers le tableau de bord
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Erreur lors de la validation de la ligue:', error);
      // En cas d'erreur réseau ou autre, effacer pour éviter les boucles
      setCurrentLeagueId(null);
      setSelectedLeague(null);
    }
  };

  // Ce composant ne rend rien
  return null;
}
